"use client";

import { CartType } from "@/app/types/cart";
import { pushEcommerceEvent, pushEvent, toRial } from "./dataLayer";
import { Ga4Item, itemFromCartItem, itemsValue } from "./items";

/**
 * Every ecommerce and lead event the site sends, in one file.
 *
 * Callers pass data the application already holds. Nothing here reads the DOM,
 * so a change to the markup can never quietly break measurement.
 */

const CURRENCY = "IRR";

/**
 * A list of products became visible — search results, a category page, a
 * carousel on the home page.
 */
export function trackViewItemList(items: Ga4Item[], listId: string, listName: string): void {
    if (!items.length) return;

    pushEcommerceEvent("view_item_list", {
        item_list_id: listId,
        item_list_name: listName,
        items,
    });
}

/**
 * A product was clicked out of one of those lists.
 */
export function trackSelectItem(item: Ga4Item, listId: string, listName: string): void {
    pushEcommerceEvent("select_item", {
        item_list_id: listId,
        item_list_name: listName,
        items: [item],
    });
}

/**
 * A product page was opened, or the shopper switched to another variant on it.
 */
export function trackViewItem(item: Ga4Item): void {
    pushEcommerceEvent("view_item", {
        currency: CURRENCY,
        value: (item.price ?? 0) - (item.discount ?? 0),
        items: [item],
    });
}

/**
 * A colour or size was chosen. GA4 has no standard name for this, but it is
 * the single most useful signal a frame shop can collect, so it is sent as a
 * custom event rather than left unmeasured.
 */
export function trackSelectVariant(item: Ga4Item): void {
    pushEcommerceEvent("select_variant", {
        currency: CURRENCY,
        value: (item.price ?? 0) - (item.discount ?? 0),
        items: [item],
    });
}

export function trackAddToCart(item: Ga4Item): void {
    pushEcommerceEvent("add_to_cart", {
        currency: CURRENCY,
        value: itemsValue([item]),
        items: [item],
    });
}

export function trackRemoveFromCart(item: Ga4Item): void {
    pushEcommerceEvent("remove_from_cart", {
        currency: CURRENCY,
        value: itemsValue([item]),
        items: [item],
    });
}

export function trackAddToWishlist(item: Ga4Item): void {
    pushEcommerceEvent("add_to_wishlist", {
        currency: CURRENCY,
        value: itemsValue([item]),
        items: [item],
    });
}

export function trackRemoveFromWishlist(item: Ga4Item): void {
    pushEcommerceEvent("remove_from_wishlist", {
        currency: CURRENCY,
        value: itemsValue([item]),
        items: [item],
    });
}

/**
 * Turns a server cart into the items and value the checkout events share.
 */
function cartPayload(cart: CartType): { items: Ga4Item[]; value: number; coupon?: string } {
    const items = (cart.items ?? []).map((item, index) => itemFromCartItem(item, { index }));

    return {
        items,
        value: toRial(cart.final_price ?? cart.total_price),
        // The API deliberately does not expose the discount code, so the title
        // is what both this file and the server send as the coupon.
        coupon: cart.discount?.title,
    };
}

export function trackViewCart(cart: CartType): void {
    const { items, value, coupon } = cartPayload(cart);
    if (!items.length) return;

    pushEcommerceEvent("view_cart", { currency: CURRENCY, value, coupon, items });
}

export function trackBeginCheckout(cart: CartType): void {
    const { items, value, coupon } = cartPayload(cart);
    if (!items.length) return;

    pushEcommerceEvent("begin_checkout", { currency: CURRENCY, value, coupon, items });
}

/**
 * A delivery method was chosen. shipping_tier is the method name, which is what
 * the report groups by.
 */
export function trackAddShippingInfo(cart: CartType, shippingTier?: string | null): void {
    const { items, value, coupon } = cartPayload(cart);
    if (!items.length) return;

    pushEcommerceEvent("add_shipping_info", {
        currency: CURRENCY,
        value,
        coupon,
        shipping_tier: shippingTier ?? undefined,
        items,
    });
}

/**
 * A payment method was chosen and the shopper committed to paying. This is the
 * last event the browser can be trusted for — the purchase itself is reported
 * by the server, once the order has been approved.
 */
export function trackAddPaymentInfo(cart: CartType, paymentType?: string | null): void {
    const { items, value, coupon } = cartPayload(cart);
    if (!items.length) return;

    pushEcommerceEvent("add_payment_info", {
        currency: CURRENCY,
        value,
        coupon,
        payment_type: paymentType ?? undefined,
        items,
    });
}

/**
 * What the shopper searched for.
 */
export function trackSearch(term: string): void {
    const trimmed = term?.trim();
    if (!trimmed) return;

    pushEvent("search", { search_term: trimmed });
}

/**
 * Lead events. None of them carries a phone number, an address or a name —
 * only the fact that the contact happened and where on the site it happened.
 */
export function trackContactClick(channel: "whatsapp" | "phone", location: string): void {
    pushEvent("contact_click", { contact_channel: channel, contact_location: location });
}

export function trackFormSubmitted(formName: string, location?: string): void {
    pushEvent("form_submitted", { form_name: formName, form_location: location });
}
